import { useMemo } from 'react';
import { useNavigate } from 'js/hooks/navigate';
import { getScript } from 'js/utils/scripts.util';
import { useIsAllowedToRedirect } from 'js/hooks/useIsAllowedToRedirect';

export const useCommonPublicAPI = () => {
    const navigate = useNavigate();
    const replace = useNavigate({ replace: true });
    const redirect = useNavigate({ redirect: true });
    const isAllowedToRedirect = useIsAllowedToRedirect();

    return useMemo(() => ({
        /**
         * Navigates to another page.
         * @memberOf Playtech.API.common
         * @function
         * @param path {String} Relative path of the existing page to redirect to.
         * @param [state] {Object} State object to store with the new navigate.
         * @param [options] {Object}
         * @param [options.removeParams=[]] {String[]} List of query string parameters to remove upon navigation.
         * @param [options.preserveSearchParams=true] {Boolean} Preserve existing query string parameters.
         * @example
         * Playtech.API.common.navigate(
         *        'test-page',
         *        { fromPopup: false },
         *        {
         *            removeParams: ['test'],
         *            preserveSearchParams: true
         *        }
         * );
         */
        navigate,
        /**
         * Navigates to another page but replaces current history frame.
         * @memberOf Playtech.API.common
         * @function
         * @param path {String} Relative path of the existing page to redirect to.
         * @param [state] {Object} State object to store with the new navigate.
         * @param [options] {Object}
         * @param [options.removeParams=[]] {String[]} List of query string parameters to remove upon navigation.
         * @param [options.preserveSearchParams=true] {Boolean} Preserve existing query string parameters.
         * @example
         * Playtech.API.common.replace(
         *        'test-page',
         *        { fromPopup: false },
         *        {
         *            removeParams: ['test'],
         *            preserveSearchParams: true
         *        }
         * );
         */
        replace,
        /**
         * Redirects to another page (full reload).
         * @memberOf Playtech.API.common
         * @function
         * @param path {String} Relative path of the existing page to redirect to.
         * @param [options] {Object}
         * @param [options.removeParams=[]] {String[]} List of query string parameters to remove upon navigation.
         * @param [options.preserveSearchParams=true] {Boolean} Preserve existing query string parameters.
         * @example
         * Playtech.API.common.redirect(
         *        'test-page',
         *       { fromPopup: false },
         *       {
         *            removeParams: ['test'],
         *            preserveSearchParams: true
         *        }
         *);
         */
        redirect,
        /**
         * Return whether the Portal can be redirected to page according to allowedRedirectHostNames.
         * @memberOf Playtech.API.common
         * @function
         * @param path {String} Relative path or full link of the existing page to redirect to.
         * @example
         * Playtech.API.common.isAllowedToRedirect('/deposit');
         * Playtech.API.common.isAllowedToRedirect('https://google.com');
         */
        isAllowedToRedirect: (path) => isAllowedToRedirect(path),
        /**
         * Load script.
         * @memberOf Playtech.API.common
         * @function
         * @param url {String} URL to JS file to load.
         * @param [parent] {String} Tag in witch script should be mounted, usage: <code>document.getElementsByTagName(parent)[0].appendChild(script)<code>.
         * @example
         * Playtech.API.common.getScript('https://www.example.com', 'head');
         */
        getScript
    }), [navigate, redirect, replace, isAllowedToRedirect]);
};
